/* Shared "raw courier scan" viewer used by both OrderTimeline and PTOrderTimeline.
 *
 * A clicked order_history item, or the "Show all scans" button, fetches the scan payload(s) PCIS
 * stored in order_events (relayed by ProShip's /api/order-scan/*), and shows them verbatim in a modal.
 * The scan(s) are matched by AWB + status_code + courier scan time (± tolerance) — see PCIS's
 * OrderEventAdminController. Nothing here refines the payload; it is displayed as received/recorded.
 */
import React, { useState } from 'react'
import {
  Box,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { parseDateAndTime } from '../../helpers/UtilityHelper'
import { getGeneric } from '../../common/constants'

/* order_history.timestamp is normally an ISO string, but a raw Mongo extended-JSON value can arrive as
 * { $date: "…" }. Normalise to a plain string so it survives the query param + backend conversion. */
export function toTimestampString(ts) {
  if (!ts) return ''
  if (typeof ts === 'string') return ts
  if (typeof ts === 'object' && ts.$date) return String(ts.$date)
  return String(ts)
}

/* Pretty-print the raw courier payload if it is JSON; otherwise show it verbatim. */
function prettyPayload(raw) {
  if (raw === null || raw === undefined || raw === '') return '—'
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch (e) {
    return String(raw)
  }
}

function sourceColor(source) {
  if (source === 'WEBHOOK_INBOUND') return 'primary'
  if (source === 'RESYNC_POLL') return 'warning'
  if (source === 'API_POLL') return 'info'
  return 'default'
}

/* One scan row (an order_events record) rendered as-is: metadata line + the raw payload block. */
function ScanCard({ scan }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5, mb: 1.5, background: '#fff' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip label={scan.source || 'UNKNOWN'} size='small' color={sourceColor(scan.source)} sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />
        <Chip label={`code ${scan.statusCode}`} size='small' variant='outlined' sx={{ height: 20, fontSize: 10 }} />
        {scan.courierStatus && (
          <Chip label={`courier: ${scan.courierStatus}`} size='small' variant='outlined' sx={{ height: 20, fontSize: 10 }} />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>#{scan.id}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}><b>Scan time:</b> {scan.eventTime || '—'}</Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}><b>Received:</b> {scan.createdAt || '—'}</Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}><b>Processed:</b> {scan.processedAt || '—'} ({scan.processingState || '—'})</Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}><b>Location:</b> {scan.location || '—'}</Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}><b>Status:</b> {scan.statusDescription || '—'}</Typography>
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}><b>Remark:</b> {scan.remark || '—'}</Typography>
      </Box>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>RAW PAYLOAD</Typography>
      <Box
        component='pre'
        sx={{
          m: 0, p: 1, background: '#0f172a', color: '#e2e8f0', borderRadius: 1,
          fontSize: 11.5, lineHeight: 1.5, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      >
        {prettyPayload(scan.rawPayload)}
      </Box>
    </Box>
  )
}

/* State + fetch logic for the scan viewer, keyed to one AWB. Returns the modal props (spread into
 * <ScanModal/>) plus the two openers a timeline wires to its button and its item links. */
export function useScanViewer(awb) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [sub, setSub] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [scans, setScans] = useState([])

  async function openScans(nextTitle, nextSub, endpoint, params) {
    setTitle(nextTitle)
    setSub(nextSub)
    setOpen(true)
    setError('')
    setScans([])
    if (!awb) {
      setError('No AWB on this order — scans are only available once an AWB is assigned.')
      return
    }
    setLoading(true)
    try {
      const res = await getGeneric(endpoint, params)
      setScans(Array.isArray(res?.data) ? res.data : [])
    } catch (e) {
      setError(e?.response?.data?.meta?.message || e?.message || 'Failed to fetch scans.')
    } finally {
      setLoading(false)
    }
  }

  const showAllScans = () =>
    openScans('All courier scans', `AWB ${awb}`, `order-scan/${encodeURIComponent(awb)}`, {})

  const showItemScan = (item) =>
    openScans(
      item.orderStatusDescription || 'Scan detail',
      `AWB ${awb} · code ${item.orderStatusCode} · ${parseDateAndTime(item.timestamp)}`,
      `order-scan/${encodeURIComponent(awb)}/item`,
      { statusCode: item.orderStatusCode, eventTime: toTimestampString(item.timestamp) },
    )

  /* Show pre-supplied scans WITHOUT a fetch — used by the Delete-and-Resync preview, whose rebuilt rows
   * already carry their raw scan inline (order_events is stale/unwritten during a dry run, so a code-based
   * /item lookup wouldn't match the re-derived status). Same ScanCard rendering, no round trip. */
  const showInlineScans = (nextTitle, nextSub, inlineScans) => {
    setTitle(nextTitle)
    setSub(nextSub)
    setOpen(true)
    setError('')
    setLoading(false)
    setScans(Array.isArray(inlineScans) ? inlineScans.filter(Boolean) : [])
  }

  return {
    showAllScans,
    showItemScan,
    showInlineScans,
    modalProps: { open, onClose: () => setOpen(false), title, sub, loading, error, scans },
  }
}

export function ScanModal({ open, onClose, title, sub, loading, error, scans }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ background: '#f7f8fa' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : error ? (
          <Alert severity='error'>{error}</Alert>
        ) : scans.length === 0 ? (
          <Alert severity='info'>
            No raw scan recorded in PCIS for this selection. Raw scans are captured only for
            PCIS-native shipments — a non-native (e.g. Clickpost) order won't have any.
          </Alert>
        ) : (
          <>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
              {scans.length} scan{scans.length > 1 ? 's' : ''} — shown exactly as received/recorded by PCIS.
            </Typography>
            {scans.map((s) => <ScanCard key={s.id} scan={s} />)}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
